import { Card, CardContent } from "./ui/card";
import { MapPin, Globe, Mail, Phone, ChevronDown } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

const DiveCenterCard = ({ diveCenterInfo, onViewMap, startExpanded = false }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(startExpanded);
  
  if (!diveCenterInfo) return null;

  const handleViewPackages = () => {
    if (diveCenterInfo.location_id) {
      navigate(`/packages?location_id=${diveCenterInfo.location_id}&dive_center_id=${diveCenterInfo.id}`);
    } else {
      navigate(`/packages?dive_center_id=${diveCenterInfo.id}`);
    }
  };

  return (
    <Card className="overflow-hidden border-0 transition-all duration-300" data-testid={`dive-center-card-${diveCenterInfo.id}`}>
      {/* Header — always visible, clickable to expand */}
      <div 
        className="relative cursor-pointer group"
        onClick={() => setExpanded(!expanded)}
      >
        {/* Logo Background */}
        <div className="bg-gradient-to-r from-cyan-600 to-blue-700 relative">
          {diveCenterInfo.image && (
            <div className="flex justify-center items-center py-6 sm:py-8">
              <img 
                src={diveCenterInfo.image} 
                alt={`${diveCenterInfo.name} logo`}
                className="h-32 sm:h-40 w-auto object-contain"
              />
            </div>
          )}
          
          {/* Name overlay */}
          <div className="absolute left-2 bottom-2 sm:bottom-3 bg-slate-900/60 backdrop-blur-sm px-2 py-1 rounded-lg border border-white/10">
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-1">{diveCenterInfo.name}</h3>
            <div className="flex items-center gap-2 text-cyan-400">
              <MapPin className="w-4 h-4" />
              <p className="text-sm sm:text-base">{diveCenterInfo.location}, {diveCenterInfo.country}</p>
            </div>
          </div>
          
          {/* Expand indicator */}
          <div className="absolute right-3 bottom-3 bg-white/10 backdrop-blur-sm rounded-full p-1.5 transition-transform duration-300" style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
            <ChevronDown className="w-5 h-5 text-white" />
          </div>
        </div>
        
        {/* Short description — always visible */}
        <CardContent className="p-4 sm:p-5">
          <p className="text-sm sm:text-base leading-relaxed line-clamp-2">
            {diveCenterInfo.short_description || diveCenterInfo.full_description}
          </p>
        </CardContent>
      </div>
      
      {/* Expandable body */}
      <div 
        className="overflow-hidden transition-all duration-500 ease-in-out"
        style={{ maxHeight: expanded ? '800px' : '0px', opacity: expanded ? 1 : 0 }}
      >
        <CardContent className="px-4 sm:px-6 pb-6 space-y-6">
          {/* Specialties */}
          {diveCenterInfo.amenities && diveCenterInfo.amenities.length > 0 && (
            <div>
              <h4 className="text-lg font-bold mb-3">SPECIALTIES</h4>
              <div className="flex flex-wrap gap-2">
                {diveCenterInfo.amenities.map((amenity, idx) => (
                  <span key={idx} className="px-3 py-1.5 bg-cyan-500 text-white rounded-full text-xs sm:text-sm font-semibold">
                    {amenity}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Contact */}
          <div>
            <h4 className="text-lg font-bold mb-3">CONTACT</h4>
            <div className="space-y-3">
              {diveCenterInfo.website && (
                <a href={diveCenterInfo.website} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 transition-colors group"
                  onClick={(e) => e.stopPropagation()}>
                  <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center group-hover:bg-cyan-500 transition-colors flex-shrink-0">
                    <Globe className="w-5 h-5 text-cyan-600 group-hover:text-white" />
                  </div>
                  <span className="text-sm sm:text-base font-medium break-all">Visit Website</span>
                </a>
              )}
              {diveCenterInfo.email && (
                <a href={`mailto:${diveCenterInfo.email}`}
                  className="flex items-center gap-3 transition-colors group"
                  onClick={(e) => e.stopPropagation()}>
                  <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center group-hover:bg-cyan-500 transition-colors flex-shrink-0">
                    <Mail className="w-5 h-5 text-cyan-600 group-hover:text-white" />
                  </div>
                  <span className="text-sm sm:text-base font-medium break-all">{diveCenterInfo.email}</span>
                </a>
              )}
              {diveCenterInfo.phone && (
                <a href={`tel:${diveCenterInfo.phone}`}
                  className="flex items-center gap-3 transition-colors group"
                  onClick={(e) => e.stopPropagation()}>
                  <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center group-hover:bg-cyan-500 transition-colors flex-shrink-0">
                    <Phone className="w-5 h-5 text-cyan-600 group-hover:text-white" />
                  </div>
                  <span className="text-sm sm:text-base font-medium">{diveCenterInfo.phone}</span>
                </a>
              )}
            </div>
          </div>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            {onViewMap && (
              <button onClick={(e) => { e.stopPropagation(); onViewMap(); }}
                className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg text-base font-bold transition-all">
                View on Map
              </button>
            )}
            <button onClick={(e) => { e.stopPropagation(); handleViewPackages(); }}
              className="flex-1 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white px-6 py-3 rounded-lg text-base font-bold transition-all">
              View Packages
            </button>
          </div>
        </CardContent>
      </div>
    </Card>
  );
};

export default DiveCenterCard;
